/*
defines common appearances and behavior for nodes,
including showing tooltips
*/

/**
 * represents the view of a node, of any kind.
 * contains common elements for all types of nodes
 */
export default class NodeView {
    /**
     * makes a new node view given a model node and a position
     *
     * @param {Node} node model node this NodeView is associated with
     * @param {number} [x] (optional) horizontal position
     * @param {number} [y] (optional) vertical pos
     */
    constructor(node, x = 0, y = 0) {
        this.x = _.toInteger(x);
        this.y = _.toInteger(y);
        this.node = node;
        this.id = this.node.id;
        this.dob = Date.now();
        this._fixed = false;
        this._shrunk = false;
        this._faded = false;

        this.width = this.defaultWidth = 160;
        this.height = this.defaultHeight = 160;

        // runs when the API is updated for this node
        this.updateCallback = () => {};

        console.log(`new node view ${this.id} at ${this.x} ${this.y}`);

    }

    /**
     *
     */
    initTooltip() {
        this.tooltip = NodeView.container.select(`.node-tooltip-${this.id}`);
    }

    /**
     *
     */
    appendTooltip() {
        let tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
        tooltip.classList.add(`node-tooltip`);
        tooltip.classList.add(`node-tooltip-${this.id}`);
        tooltip.setAttribute('height', '200');
        tooltip.setAttribute('width', '200');
        tooltip.setAttribute('x', this.x + this.width / 2 + 10);
        tooltip.setAttribute('y', this.y - this.height / 2);
        return tooltip;
    }

    /**
     * shows the given tooltip with information about this node at its location
     * @param {Selection} tooltip the selection of the tooltip to show
     */
    showTooltip() {
        if (!this.node.isEmpty()) {
            this.updateTooltip();
            this.tooltip
                // .raise()
                .attr('x', (d) => (d.x + d.width + 10))
                .attr('y', (d) => (d.y))
                // .attr('data-node', this.node.id)
                .transition()
                .style('opacity', .8)
                .on('end', () => this.tooltip.style('display', 'block'));
        }
    }

    /**
     * toggles the visibility of this node's tooltip
     */
    toggleTooltip() {
        if (!this.tooltip || this.tooltip.empty()) {
            this.initTooltip();
        }
        if (this.isTooltipShown()) {
            this.hideTooltip();
        } else {
            this.showTooltip();
        }
    }

    /**
     * checks if this node's tooltip is shown
     * @return {Boolean}
     */
    isTooltipShown() {
        return this.tooltip.style('display') === 'block';
    }

    /**
     * updates the given tooltip with the text from this node
     * @param {Selection} tooltip the d3 selection of the tooltip
     */
    updateTooltip() {
        this.tooltipText = this.node.metadata;
        let pre = document.createElement('pre');
        pre.setAttribute('contenteditable', 'true');
        this.tooltip.select('pre').remove();
        pre.innerHTML = this.tooltipText;
        this.tooltip.append(() => pre);
    }

    /**
     * hides the given tooltip
     */
    hideTooltip() {
        this.tooltip.lower()
                .transition()
                .style('opacity', 0)
                .on('end', () => this.tooltip.style('display', 'none'));
    }

    /**
     * defines the DOM selections for the given nodes, styles them and places them in the svg
     * @param {Array<LinkView>} nodes list of NodeViews to initialize with
     */
    static init(nodes) {
        NodeView.svg = d3.select('svg');
        NodeView.container = d3.select('.everything');

        NodeView.g_selection = 
            NodeView.container
            .selectAll('.gnode')
            .data(nodes)
            .enter()
            .append('g')
                .attr('class', (d) => `gnode gnode--${d.type} gnode-${d.id}`)
                // .attr('style', (d) => `node--${d.apiSource.style}`);
        NodeView.node_selection = NodeView.g_selection
            .append(function(d) {
                return d.append();
            })
            .attr('class', (d) => d.getClass());

        NodeView.tooltip_selection = NodeView.g_selection
            .append(function(d) {
                    return d.appendTooltip();
            });

        let r = 10;

        NodeView.plus_button_selection = NodeView.g_selection
            .append('text')
            .attr('x', (d) => d.x + (d.width / 2) - 10 - r)
            .attr('y', (d) => d.y - (d.width / 2) + 10 + r)
            .attr('fill', 'white')
            .text('+');

        NodeView.text_selection = NodeView.container.selectAll('.text');
        NodeView.style();

        NodeView.width = 160;
        NodeView.height = 160;
        NodeView.shrinkSize = .5;

        NodeView.clipSelection = {};
    }

    /**
     * adds style attributes to all viewable nodes
     */
    static style() {
        NodeView.node_selection
            .attr('width', (d) => d.width)
            .attr('height', (d) => d.height)
            .style('fill', (d) => d.fill(d.id));
    }

    /**
     * given a new list of nodes,
     * adds or removes the necessary elements to/from the view
     *
     * @param {Array<NodeView>} nodes the new list of node view
     */
    static restart(nodes) {
        NodeView.g_selection = NodeView.g_selection
            .data(nodes, (d) => d.id);
        NodeView.g_selection.exit().remove();
        let gnodesEntered = NodeView.g_selection
            .enter()
            .append('g')
            .attr('class', (d) => `gnode gnode--${d.type} gnode-${d.id}`)
        gnodesEntered.append(function(d) {
                return d.append();
            })
            .attr('class', (d) => d.getClass());
        gnodesEntered.append(function(d) {
            return d.appendTooltip();
        });

        let r = 10;


        // gnodesEntered
        //     .append('circle')
        //     .attr('cx', (d) => d.x + (d.width / 2) - 5 - r)
        //     .attr('cy', (d) => d.y - (d.width / 2) + 5 + r)
        //     .attr('fill', 'black')
        //     .attr('r', r)
        //     .style('opacity', '.5');

        gnodesEntered
            .append('text')
            .attr('class', (d) => `node-plus node-plus-${d.id}`)
            .attr('x', (d) => d.x + (d.width / 2) - 10 - r)
            .attr('y', (d) => d.y - (d.width / 2) + 10 + r)
            .attr('fill', 'white')
            .text('+');

        NodeView.g_selection = gnodesEntered.merge(NodeView.g_selection);
        NodeView.node_selection = NodeView.g_selection.selectAll('.node');
        NodeView.plus_button_selection = NodeView.g_selection.selectAll('.node-plus');
        NodeView.tooltip_selection = NodeView.g_selection.selectAll('.node-tooltip');
        NodeView.style();
    }

    /**
     * gets the class name for this node
     */
    getClass() {
        let style = (this.node.apiSource && this.node.apiSource.style )? this.node.apiSource.style : 'defaultApiStyle';
        return `node node--${this.type} node-${this.id} node--${style}`;
    }

    /**
     * updates the position of each node
     * runs every frame
     */
    static onTick() {
        NodeView.node_selection
            .attr('x', (d) => d.x)
            .attr('y', (d) => d.y)
            .classed('off', (d) => !!d._hidden)
            .classed('shrunk', (d) => !!d._shrunk)
            .classed('fixed', (d) => !!d._fixed)
            .classed('faded', (d) => !!d._faded)
            .classed('dragging', (d) => !!d._dragging);
        NodeView.tooltip_selection
            .attr('x', (d) => d.x + d.width / 2 + 10)
            .attr('y', (d) => d.y - d.height / 2);
        let r = 10;
        NodeView.plus_button_selection
            .attr('x', (d) => d.x + (d.width / 2) - 10 - r)
            .attr('y', (d) => d.y - (d.height / 2) + 10 + r);
    }

    /**
     * updates the size of this node if being resized
     */
    static updateSize() {
        NodeView.node_selection
            .transition()
            .attr('x', (d) => d.x)
            .attr('y', (d) => d.y)
            .attr('width', (d) => d.width)
            .attr('height', (d) => d.height)
            .style('transform', (d) => `translateX(${d.shrunk ? - d.width / 4 : - d.width / 2}px) translateY(${d.shrunk ? - d.height / 2 : - d.height / 2}px) scale(${d.shrunk ? NodeView.shrinkSize : 1})`);
    }

    /**
     * returns the svg object that should be created for each node
     * @return {SVGElement}
     */
    append() {
        return document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    }

    /**
     * gets the default fill that a node should have
     * @return {String}
     */
    fill() {
        return 'lightgrey';
    }

    /**
     * makes this node fixed (relative to physics)
     *
     */
    fix() {
        this._fixed = true;
        this.fx = this.x;
        this.fy = this.y;
    }

    /**
     * makes this node unfixed (relative to physics)
     */
    unfix() {
        this._fixed = false;
        this.fx = null;
        this.fy = null;
    }

    /**
     * shrink this node
     */
    shrink() {
        this.shrunk = true;
    }

    /**
     * grows this node on hover if it is small
     */
    hover() {
        if (this.isFollowing) this.grow();
    }

    /**
     * shrinks this node on mouse out if it was small before
     */
    unhover() {
        if (this.isFollowing) this.shrink();
    }

    /**
     * grows this node
     */
    grow() {
        this.shrunk = false;
    }

    /**
     * show this node at half-opacity
     */
    fade() {
        this._faded = true;
    }

    /**
     * show this node at full opacity
     */
    unfade() {
        this._faded = false;
    }

    /**
     * show this node
     */
    show() {
        this.unfade();
    }

    /**
     * checks if this node is shrunken in size
     */
    get shrunk() {
        return this._shrunk;
    }

    /**
     * sets this node to be shrunken or not depending on the given value
     */
    set shrunk(shrinking) {
        this._shrunk = shrinking;
        NodeView.updateSize();
    }

    /**
     * hides this node
     */
    hide() {
        this._hidden = true;
    }

    /**
     * shows this node
     */
    show() {
        this._hidden = false;
    }

    /**
     * gets the D3 selection for this node
     */
    getSelection() {
        this.selection = (!this.selection || this.selection.empty()) ? d3.select(`.node-${this.id}`) : this.selection;
        return this.selection;
    }

}
